/**
 * Inline SVG glyphs for the mobile `ds-*` organism track.
 *
 * CSS selector: none, these render inside organism blocks and inherit
 * `currentColor` from them.
 *
 * Doctrine: mobile icons are inline SVG on a 24 grid, Feather style,
 * `stroke="currentColor"`, stroke-width 1.7 to 1.8 for list icons and 2 to
 * 2.25 for chevrons and back arrows. The mobile track does NOT use Phosphor,
 * that is the desktop `dsk-*` track's `<Icon>` primitive. Every glyph here is
 * decorative and therefore `aria-hidden`, the accessible name always lives on
 * the control that wraps it.
 *
 * Not exported from the barrel on purpose. These are internal to the organism
 * layer, the registry has no entry for them and they must not become a public
 * icon API competing with the atoms layer.
 */

const LIST_STROKE = 1.8;
const CHEVRON_STROKE = 2.25;

function base(size) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
    focusable: 'false',
  };
}

/** Wishlist heart. Fills from `.ds-heart[aria-pressed="true"] svg`, so the
 *  resting glyph must stay `fill="none"` and let the CSS do the filling. */
export function HeartGlyph({ size = 18, filled = false }) {
  return (
    <svg {...base(size)} strokeWidth={LIST_STROKE} fill={filled ? 'currentColor' : 'none'}>
      <path d="M12 20.5S4.5 15.9 2 11.2C.5 8 2 4.5 5.4 3.7c2.1-.5 4.2.4 5.4 2.1a1 1 0 0 0 1.4 0c1.2-1.7 3.3-2.6 5.4-2.1C21 4.5 22.5 8 21 11.2c-2.5 4.7-9 9.3-9 9.3Z" />
    </svg>
  );
}

/** Chevron. `.ds-accordion__trigger[aria-expanded="true"] svg` rotates the
 *  down chevron 180deg, so the accordion always passes `direction="down"`. */
export function ChevronGlyph({ size = 20, direction = 'right' }) {
  const d = {
    down: 'M6 9.5 12 15.5 18 9.5',
    up: 'M6 14.5 12 8.5 18 14.5',
    left: 'M14.5 6 8.5 12 14.5 18',
    right: 'M9.5 6 15.5 12 9.5 18',
  }[direction];
  return (
    <svg {...base(size)} strokeWidth={CHEVRON_STROKE}>
      <path d={d} />
    </svg>
  );
}

/** Back arrow, used by the sheet drill-in header when no `<BackButton>` atom
 *  is supplied by the caller. */
export function ArrowLeftGlyph({ size = 20 }) {
  return (
    <svg {...base(size)} strokeWidth={CHEVRON_STROKE}>
      <path d="M19 12H5" />
      <path d="M11 18 5 12l6-6" />
    </svg>
  );
}

/** Sliders glyph for `.ds-filterbtn`. */
export function FilterGlyph({ size = 18 }) {
  return (
    <svg {...base(size)} strokeWidth={LIST_STROKE}>
      <path d="M4 7h11" />
      <path d="M19 7h1" />
      <path d="M4 17h5" />
      <path d="M13 17h7" />
      <circle cx="17" cy="7" r="2" />
      <circle cx="11" cy="17" r="2" />
    </svg>
  );
}

/**
 * Rating star.
 *
 * Doctrine: the earned portion is FILLED and the remainder is OUTLINE, and a
 * star is never yellow. `fraction` is 0 to 1 for this single star, so a 4.6
 * rating renders four solid stars, one 60 percent star and no rounding lie.
 * The fill is painted through a per-star gradient rather than by rounding,
 * because rounding is what produces the "4.6 shows as 5" class of bug.
 */
export function StarGlyph({ size = 16, fraction = 1, gradientId }) {
  const clamped = Math.max(0, Math.min(1, fraction));
  const solid = clamped >= 0.999;
  const empty = clamped <= 0.001;
  const path =
    'M12 2.6l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.6l-6.1 3.6 1.4-6.8L2.2 9.7l6.9-.8L12 2.6Z';

  if (solid || empty) {
    return (
      <svg {...base(size)} strokeWidth={1.5} fill={solid ? 'currentColor' : 'none'}>
        <path d={path} />
      </svg>
    );
  }

  return (
    <svg {...base(size)} strokeWidth={1.5}>
      <defs>
        <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
          <stop offset={clamped} stopColor="currentColor" />
          <stop offset={clamped} stopColor="transparent" />
        </linearGradient>
      </defs>
      <path d={path} fill={`url(#${gradientId})`} />
    </svg>
  );
}

/** Compact solid star for `.ds-listing-row__rating`, which is a figure and not
 *  a rating control, so it does not take `outOf`. */
export function StarSolidGlyph({ size = 12 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 2.6l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.6l-6.1 3.6 1.4-6.8L2.2 9.7l6.9-.8L12 2.6Z" />
    </svg>
  );
}
